import { type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'muted' | 'accent';
type BadgeSize = 'sm' | 'md';
type BadgeShape = 'pill' | 'tag';

const TONE_BG_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-zoovpalelavender',
  success: 'bg-zoosuccessbg',
  warning: 'bg-zoowarningbg',
  danger: 'bg-zooerrorbg',
  muted: 'bg-zoodisabledbg',
  accent: 'bg-zooindigo',
};

const TONE_TEXT_CLASS: Record<BadgeTone, string> = {
  neutral: 'text-zooprimary',
  success: 'text-zoosuccesstext',
  warning: 'text-zoowarningtext',
  danger: 'text-zooerrortext',
  muted: 'text-zoodisabledtext',
  accent: 'text-zoowhite',
};

const TONE_BORDER_CLASS: Record<BadgeTone, string> = {
  neutral: 'border border-zooprimary border-opacity-30',
  success: 'border border-zoosuccesstext border-opacity-30',
  warning: 'border border-zoowarningtext border-opacity-30',
  danger: 'border border-zooerrortext border-opacity-30',
  muted: 'border border-zoodisabledtext border-opacity-30',
  accent: 'border border-zooindigo',
};

const SIZE_CLASS: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-1',
};

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  size?: BadgeSize;
  shape?: BadgeShape;
  uppercase?: boolean;
  outline?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Badge({
  children,
  tone = 'neutral',
  size = 'sm',
  shape = 'pill',
  uppercase = false,
  outline = false,
  className,
  style,
}: BadgeProps) {
  return (
    <span
      style={style}
      className={cn(
        'inline-flex items-center font-bold whitespace-nowrap',
        shape === 'pill' ? 'rounded-full' : 'rounded-md',
        SIZE_CLASS[size],
        TONE_TEXT_CLASS[tone],
        outline === true ? cn('bg-zoowhite', TONE_BORDER_CLASS[tone]) : TONE_BG_CLASS[tone],
        uppercase === true ? 'uppercase tracking-wider' : '',
        className
      )}
    >
      {children}
    </span>
  );
}
